import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { SxProps, Tooltip } from "@mui/material";
import type { DatePickerProps } from "@mui/x-date-pickers";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useAdapterLocale } from "hooks/useAdapterLocale";
import type { CSSProperties } from "react";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";

import variables from "../../../assets/styles/_variables.module.scss";
import StyledTextField from "../StyledTextField/StyledTextField";

const getCalendarPickerIconSx = (inputSize?: "small" | "medium") => ({
    fontSize: inputSize === "small" ? 12 : 14,
    width: inputSize === "small" ? 12 : 14,
    height: inputSize === "small" ? 12 : 14,
});

/** Calendar picker button only (disabled fields use the lock icon, not IconButton). */
export const getDatePickerEndAdornmentSx = (inputSize?: "small" | "medium") => ({
    "& .MuiInputAdornment-positionEnd .MuiIconButton-root": {
        padding: 0,
        marginRight: inputSize === "small" ? "2px" : "1px",
    },
    ...(inputSize === "small" && {
        "& .MuiInputAdornment-positionEnd .MuiIconButton-root .MuiSvgIcon-root": {
            fontSize: "12px",
            width: "12px",
            height: "12px",
        },
    }),
});

/** @deprecated Prefer getDatePickerEndAdornmentSx(inputSize) */
export const datePickerEndAdornmentSx = getDatePickerEndAdornmentSx();

const getLockIconSx = (inputSize?: "small" | "medium") => ({
    color: "#888",
    height: inputSize === "small" ? "12px" : "15px",
    width: inputSize === "small" ? "12px" : "15px",
    marginRight: 0,
    cursor: "pointer",
});

const datePicker = {
    "& .MuiPaper-root": {
        ".MuiCalendarPicker-root": {
            ".MuiPickersDay-root": {
                "&:hover": {
                    backgroundColor: variables["purple-op-20"],
                },
                "&.Mui-selected": {
                    backgroundColor: variables["primary-purple"],
                    "&:hover": {
                        backgroundColor: variables["primary-purple-dark"],
                    },
                },
            },
            ".MuiMonthPicker-root": {
                "button:hover": {
                    backgroundColor: variables["purple-op-20"],
                },
                ".Mui-selected": {
                    backgroundColor: variables["primary-purple"],
                    "&:hover": {
                        backgroundColor: variables["primary-purple-dark"],
                    },
                },
            },
            ".MuiYearPicker-root": {
                "button:hover": {
                    backgroundColor: variables["purple-op-20"],
                },
                ".Mui-selected": {
                    backgroundColor: variables["primary-purple"],
                    "&:hover": {
                        backgroundColor: variables["primary-purple-dark"],
                    },
                },
            },
        },
    },
};

interface StyledDatePickerProps extends Omit<DatePickerProps<any, any>, "renderInput"> {
    /**
     * Maximum number of years after current date that are displayed. By default max is 1.
     */
    maxYearDistance?: number;
    /**
     * Date from which to start counting maxYears. By default it is current date.
     */
    maxYear?: number;
    /**
     * Max date that they can select.
     */
    maxDate?: Date;
    disabledText?: string;
    disabledTooltipStyle?: CSSProperties;
    helperText?: string;
    error?: boolean;
    textFieldSx?: any;
    renderInput?: any;
    /** Matches TextField `size` so the calendar icon and spacing scale down in small fields. */
    inputSize?: "small" | "medium";
}

export const StyledDatePicker = forwardRef<any, StyledDatePickerProps>(function StyledDatePicker(props, ref) {
    const {
        maxYearDistance = 1,
        maxYear = new Date().getFullYear(),
        maxDate,
        disabled,
        disabledText,
        helperText,
        error,
        textFieldSx,
        renderInput,
        inputSize,
        ...rest
    } = props;
    const adapterLocale = useAdapterLocale();
    const [t] = useTranslation();

    // You take current day, and you set the year passed, then change date to 31st of december.
    // So when passing a maxYear, the date limit will always be the end of that year.
    const nbYearsFromNow = new Date();
    nbYearsFromNow.setFullYear(maxYear + maxYearDistance);
    nbYearsFromNow.setMonth(11, 31); // 11 is December.

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
            <DatePicker
                {...rest}
                ref={ref}
                disabled={disabled}
                components={{
                    OpenPickerIcon: () => <CalendarTodayOutlinedIcon sx={getCalendarPickerIconSx(inputSize)} />,
                }}
                PopperProps={{
                    sx: datePicker,
                }}
                // If you pass maxDate, that is the set limit. Otherwise we allow to choose up to the 31st
                // of december of current year (or year passed with maxYear)
                maxDate={maxDate ?? nbYearsFromNow}
                renderInput={(params) =>
                    renderInput ? (
                        renderInput(params)
                    ) : (
                        <StyledTextField
                            {...params}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: disabled ? (
                                    <Tooltip arrow title={disabledText ?? t("Generic.DisabledField")}>
                                        <LockOutlinedIcon sx={getLockIconSx(inputSize)} />
                                    </Tooltip>
                                ) : (
                                    params.InputProps?.endAdornment
                                ),
                            }}
                            sx={{ ...getDatePickerEndAdornmentSx(inputSize), ...textFieldSx }}
                            disabled={disabled}
                            error={error || params.error}
                            helperText={helperText}
                        />
                    )
                }
            />
        </LocalizationProvider>
    );
});
